import React from 'react';
import API from '@beardeddevops/react.api';
import Framework from '@beardeddevops/react.framework';
import Types from 'constants/Types';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Tooltip from '@material-ui/core/Tooltip';
import ReactTable from 'react-table';
import Select from 'react-select';
import {Button, Modal, ModalBody, ModalFooter} from 'reactstrap';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import moment from 'moment';
import {Link} from 'react-router-dom';
import {
	buildFeDayEtas,
	formatScheduledSlot,
	formatStatusTime,
	regionRankingValue,
	resolveSlotLabel,
	sortFesByRegionThenName,
} from 'constants/AppointmentEta';
import UserRoles from 'constants/UserRoles';
import EngineerHDCard from 'components/Parts/EngineerHDCard';
import {formatTicketProgressText} from 'components/Parts/TicketProgressDisplay';

const ETA_COLOR_LEGEND = [
	{
		key: 'pending',
		swatch: '#e3f2fd',
		border: '#90caf9',
		label: 'Pending SoD',
		description: 'Start of day not recorded yet; ETA cannot be projected',
	},
	{
		key: 'green',
		swatch: '#c8e6c9',
		border: '#81c784',
		label: 'Early',
		description: 'Projected or actual arrival more than 15 min before slot end',
	},
	{
		key: 'ontime',
		swatch: '#ffffff',
		border: '#cfd8dc',
		label: 'On time',
		description: 'Within 15 min of slot end (including slightly early)',
	},
	{
		key: 'yellow',
		swatch: '#fff9c4',
		border: '#fff176',
		label: 'Late',
		description: '1–29 min past slot end',
	},
	{
		key: 'red',
		swatch: '#ffcdd2',
		border: '#e57373',
		label: 'Very late',
		description: '30+ min past slot end',
	},
];

/**
 * Informational Daily ETA board for today's schedule.
 * Dispatch+ can filter by region/FE; Field Engineers see themselves only.
 */
export default class DailyEtaBoard extends Framework.Components.Collection {
	constructor(props) {
		super(props, Types.Users._name);
		const params = new URLSearchParams(props.location?.search || '');
		const feParam = params.get('fe');
		const isFeOnly =
			props.feSelfView === true ||
			(Array.isArray(props.roles) &&
				props.roles.includes(UserRoles.FieldEngineer) &&
				!props.roles.some((r) =>
					[UserRoles.Admin, UserRoles.Manager, UserRoles.Dispatch, UserRoles.HelpDesk].includes(r)
				));

		this.state = {
			...this.state,
			loading: false,
			items: [],
			filterDate: moment().format('YYYY-MM-DD'),
			regions: [],
			selectedRegions: [],
			selectedFes: feParam ? [{value: Number(feParam), label: `FE #${feParam}`}] : [],
			feSelectOptions: [],
			extendColumns: false,
			isFeOnly,
			pollMs: 60000,
			show_hd_modal: false,
			field_engineer: null,
			legendOpen: true,
			stopPopover: null,
		};
		this._poll = null;
	}

	async componentDidMount() {
		this._ismounted = true;
		if (!this.state.isFeOnly) {
			await Promise.all([this.loadRegions(), this.loadFieldEngineers()]);
		}
		await this.fetchDailyEta();
		this._poll = setInterval(() => {
			this.fetchDailyEta(true);
		}, this.state.pollMs);
	}

	componentWillUnmount() {
		this._ismounted = false;
		if (this._poll) clearInterval(this._poll);
	}

	loadRegions = async () => {
		const model = new API.BuildClass(Types.Regions._name, API.RequestType.READ_ALL);
		model.limit = 5000;
		model.filter = {filterMetroWalk: true};
		try {
			let regions = await model.submit(null, 'GET_ALL');
			regions = Object.keys(regions).map((key) => regions[key]);
			regions = regions[3] || [];
			regions = regions.sort(
				(a, b) =>
					regionRankingValue(a) - regionRankingValue(b) ||
					(a.name || '').localeCompare(b.name || '')
			);
			const options = regions.map((r) => ({
				value: r.pkey,
				label: r.name || r.abbreviation || `Region ${r.pkey}`,
			}));
			if (this._ismounted) {
				this.setState({regions: options});
			}
		} catch (e) {
			console.warn('Daily ETA regions load failed', e);
		}
	};

	buildGroupedFeOptions = (fes) => {
		const groups = new Map();
		(fes || []).forEach((fe) => {
			const groupLabel =
				fe.region && fe.regions?.name ? fe.regions.name : 'No Region';
			const ranking = regionRankingValue(fe.regions);
			if (!groups.has(groupLabel)) {
				groups.set(groupLabel, {ranking, options: []});
			}
			groups.get(groupLabel).options.push({
				value: fe.pkey,
				label: `${fe.name_first || ''} ${fe.name_last || ''}`.trim(),
			});
		});

		return [...groups.entries()]
			.sort((a, b) => a[1].ranking - b[1].ranking)
			.map(([label, group]) => {
				group.options.sort((a, b) => a.label.localeCompare(b.label));
				return {label, options: group.options};
			});
	};

	loadFieldEngineers = async () => {
		const model = new API.BuildClass(Types.Users._name, API.RequestType.READ);
		model.limit = null;
		model.sort = 'ranking';
		model.filter = {bookableOnly: true};
		try {
			let fes = await model.submit(null, 'GET_FIELD_ENGINEERS');
			fes = Object.keys(fes).map((key) => fes[key]);
			fes = fes[3] || [];
			const feSelectOptions = this.buildGroupedFeOptions(fes);
			const flatOptions = feSelectOptions.flatMap((group) => group.options);
			let selectedFes = this.state.selectedFes;
			if (selectedFes.length && String(selectedFes[0].label).startsWith('FE #')) {
				const match = flatOptions.find((o) => o.value === selectedFes[0].value);
				if (match) selectedFes = [match];
			}
			if (this._ismounted) {
				this.setState({feSelectOptions, selectedFes});
			}
		} catch (e) {
			console.warn('Daily ETA field engineers load failed', e);
		}
	};

	getHelpdesks = async (engineerPkey) => {
		const model = new API.BuildClass(Types.Users_Help_Desk._name, API.RequestType.READ);
		model.filter = {
			day: moment().format(),
			users: [engineerPkey],
			shouldTrack: false,
		};
		try {
			let data = await model.submit(null, 'GET_ALL');
			data = data?.items ?? data;
			this.setState({
				field_engineer: data?.[0] ?? null,
				show_hd_modal: true,
			});
		} catch (e) {
			console.warn('Daily ETA helpdesk load failed', e);
		}
	};

	toggleHdModal = () => {
		this.setState({show_hd_modal: false, field_engineer: null});
	};

	openStopPopover = (feName, stop) => {
		if (!stop) return;
		this.setState({stopPopover: {feName, stop}});
	};

	closeStopPopover = () => {
		this.setState({stopPopover: null});
	};

	fetchDailyEta = async (silent = false, filterOverrides = {}) => {
		if (!this._ismounted) return;
		if (!silent) this.setState({loading: true});
		const selectedRegions =
			filterOverrides.selectedRegions !== undefined
				? filterOverrides.selectedRegions
				: this.state.selectedRegions;
		const selectedFes =
			filterOverrides.selectedFes !== undefined
				? filterOverrides.selectedFes
				: this.state.selectedFes;
		try {
			const model = new API.BuildClass(Types.Users._name, API.RequestType.READ);
			const filter = {
				filterDate: this.state.filterDate,
			};
			if (this.state.isFeOnly && this.props.userPkey) {
				filter.filterUsers = [this.props.userPkey];
			} else {
				if (selectedFes?.length) {
					filter.filterUsers = selectedFes.map((o) => o.value);
				}
				if (selectedRegions?.length) {
					filter.filterRegions = selectedRegions.map((o) => o.value);
				}
			}
			model.filter = filter;
			const resp = await model.submit(null, 'GET_DAILY_ETA');
			const rawItems = (Array.isArray(resp?.items) ? resp.items : []).filter(
				(fe) => Array.isArray(fe.stops) && fe.stops.length > 0
			);
			const sorted = sortFesByRegionThenName(rawItems);
			const withEtas = sorted.map((fe) => {
				const built = buildFeDayEtas(fe);
				return {
					...fe,
					sodDisplay: built.sod ? formatStatusTime(built.sod) : 'N/A',
					etaStops: built.stops,
				};
			});
			if (this._ismounted) {
				this.setState({
					items: withEtas,
					loading: false,
				});
			}
		} catch (e) {
			console.warn('Daily ETA fetch failed', e);
			if (this._ismounted) this.setState({loading: false});
		}
	};

	formatProgress = (progress) => formatTicketProgressText(progress);

	getScheduledSlotDisplay = (stop) => {
		if (!stop) return null;
		return formatScheduledSlot(stop.time_slot || resolveSlotLabel(stop));
	};

	renderStopTooltip = (feName, stop) => {
		if (!stop) return null;
		const onsite = !!(stop.onsite_at || stop.actual);
		const scheduledSlot = this.getScheduledSlotDisplay(stop);
		return (
			<div style={{fontSize: 12, lineHeight: 1.5, padding: '2px 0'}}>
				<div style={{fontWeight: 600, marginBottom: 4}}>
					{feName} - {stop.customer_name || 'Appointment'}
				</div>
				{scheduledSlot ? (
					<div>
						<strong>Scheduled:</strong> {scheduledSlot}
					</div>
				) : null}
				{!onsite || !stop.onsite_at ? (
					<>
						<div>
							<strong>Drive:</strong>{' '}
							{stop.remote
								? 'Remote (0 min)'
								: stop.drive_to != null
									? `${stop.drive_to} min`
									: '-'}
						</div>
						<div>
							<strong>Progress:</strong> {this.formatProgress(stop.progress)}
						</div>
						{stop.etaDisplay ? (
							<div>
								<strong>ETA:</strong> {stop.etaDisplay}
								{stop.actual ? ' (arrived)' : ' (projected)'}
							</div>
						) : stop.pendingSod ? (
							<div>
								<strong>ETA:</strong> pending SoD
							</div>
						) : null}
					</>
				) : (
					<>
						<div>
							<strong>Arrived:</strong> {formatStatusTime(stop.onsite_at) || '-'}
						</div>
						<div>
							<strong>Offsite:</strong>{' '}
							{stop.offsite_at ? formatStatusTime(stop.offsite_at) : '-'}
						</div>
						<div>
							<strong>TOS:</strong> {stop.time_on_site || '-'}
						</div>
						<div>
							<strong>Progress:</strong> {this.formatProgress(stop.progress)}
						</div>
					</>
				)}
			</div>
		);
	};

	etaCellStyle = (stop, linked = false) => {
		const cursor = linked ? 'pointer' : 'default';
		if (stop.pendingSod) {
			return {backgroundColor: '#e3f2fd', cursor, fontStyle: 'italic'};
		}
		const color = stop.color;
		if (color === 'green') return {backgroundColor: '#c8e6c9', cursor};
		if (color === 'yellow') return {backgroundColor: '#fff9c4', cursor};
		if (color === 'red') return {backgroundColor: '#ffcdd2', cursor};
		return {cursor};
	};

	buildTableData = () => {
		const rows = [];
		let lastRegionKey = null;
		for (const fe of this.state.items) {
			const regionKey = fe.region ?? fe.region_name ?? 'none';
			const regionLabel = fe.region_name || fe.region_abbreviation || 'No Region';
			if (regionKey !== lastRegionKey) {
				rows.push({
					_rowType: 'region',
					_key: `region-${regionKey}`,
					regionLabel,
				});
				lastRegionKey = regionKey;
			}
			rows.push({...fe, _rowType: 'fe', _key: `fe-${fe.pkey}`});
		}
		return rows;
	};

	buildColumns = () => {
		const maxStops = this.state.extendColumns
			? Math.max(5, ...this.state.items.map((fe) => (fe.etaStops || []).length), 5)
			: 5;

		const columns = [
			{
				Header: 'Engineer',
				id: 'name',
				accessor: (obj) => obj.name,
				width: 180,
				Cell: (row) => {
					if (row.original._rowType === 'region') {
						return (
							<span
								style={{
									fontWeight: 700,
									textTransform: 'uppercase',
									color: '#555',
									letterSpacing: '0.03em',
								}}
							>
								{row.original.regionLabel}
							</span>
						);
					}
					if (this.state.isFeOnly) {
						return (
							<span>
								<strong>{row.original.name}</strong>
								{row.original.region_abbreviation ? (
									<span style={{color: '#666', marginLeft: 6}}>
										({row.original.region_abbreviation})
									</span>
								) : null}
							</span>
						);
					}
					return (
						<span
							style={{cursor: 'pointer', color: '#1976d2'}}
							title="Show full day"
							onClick={() => this.getHelpdesks(row.original.pkey)}
						>
							<strong>{row.original.name}</strong>
							{row.original.region_abbreviation ? (
								<span style={{color: '#666', marginLeft: 6}}>
									({row.original.region_abbreviation})
								</span>
							) : null}
						</span>
					);
				},
			},
			{
				Header: 'SoD',
				id: 'sod',
				accessor: (obj) => obj.sodDisplay,
				width: 90,
				Cell: (row) => {
					if (row.original._rowType === 'region') return null;
					return row.original.sodDisplay;
				},
			},
		];

		for (let i = 0; i < maxStops; i++) {
			const ordinal = i + 1;
			const suffix =
				ordinal === 1 ? 'st' : ordinal === 2 ? 'nd' : ordinal === 3 ? 'rd' : 'th';
			columns.push({
				Header: `ETA ${ordinal}${suffix}`,
				id: `eta_${ordinal}`,
				width: 140,
				accessor: (obj) =>
					obj.etaStops && obj.etaStops[i]
						? obj.etaStops[i].cellDisplay || obj.etaStops[i].etaDisplay
						: '',
				Cell: (row) => {
					if (row.original._rowType === 'region') return null;
					const stop = row.original.etaStops && row.original.etaStops[i];
					if (!stop) return <span style={{color: '#bbb'}}>-</span>;
					const display = stop.cellDisplay || stop.etaDisplay || '-';
					const linked = !!stop.ticket;
					const cellContent = (
						<span style={this.etaCellStyle(stop, this.state.isFeOnly ? true : linked)}>
							{display}
							{stop.actual ? ' *' : ''}
						</span>
					);
					if (this.state.isFeOnly) {
						return (
							<span
								role="button"
								tabIndex={0}
								style={{display: 'block'}}
								onClick={(event) => {
									event.stopPropagation();
									this.openStopPopover(row.original.name, stop);
								}}
								onKeyDown={(event) => {
									if (event.key === 'Enter' || event.key === ' ') {
										event.preventDefault();
										this.openStopPopover(row.original.name, stop);
									}
								}}
							>
								{cellContent}
							</span>
						);
					}
					return (
						<Tooltip
							title={this.renderStopTooltip(row.original.name, stop)}
							arrow
							placement="top"
							interactive
						>
							{linked ? (
								<Link
									to={`/dispatch/calendar/${stop.ticket}`}
									target="_blank"
									rel="noopener noreferrer"
									style={{textDecoration: 'none', color: 'inherit', display: 'block'}}
								>
									{cellContent}
								</Link>
							) : (
								cellContent
							)}
						</Tooltip>
					);
				},
			});
		}
		return columns;
	};

	renderLegendSection = () => {
		if (!this.state.isFeOnly) {
			return this.renderColorLegend();
		}
		return (
			<div style={{marginBottom: 12}}>
				<button
					type="button"
					onClick={() => this.setState({legendOpen: !this.state.legendOpen})}
					style={{
						fontSize: 12,
						fontWeight: 600,
						color: '#1976d2',
						background: 'none',
						border: 'none',
						padding: 0,
						marginBottom: this.state.legendOpen ? 8 : 0,
						cursor: 'pointer',
					}}
				>
					{this.state.legendOpen ? 'Hide color legend' : 'Show color legend'}
				</button>
				{this.state.legendOpen ? this.renderColorLegend() : null}
			</div>
		);
	};

	renderColorLegend = () => (
		<div
			style={{
				display: 'flex',
				flexWrap: 'wrap',
				gap: '12px 20px',
				marginBottom: this.state.isFeOnly ? 0 : 12,
				padding: '10px 12px',
				background: '#fafafa',
				border: '1px solid #e0e0e0',
				borderRadius: 4,
			}}
		>
			<span style={{fontSize: 12, fontWeight: 600, color: '#444', alignSelf: 'center'}}>
				ETA colors vs slot end:
			</span>
			{ETA_COLOR_LEGEND.map((item) => (
				<div
					key={item.key}
					style={{display: 'flex', alignItems: 'flex-start', gap: 8, maxWidth: 280}}
				>
					<span
						aria-hidden
						style={{
							width: 18,
							height: 18,
							marginTop: 1,
							flexShrink: 0,
							backgroundColor: item.swatch,
							border: `1px solid ${item.border}`,
							borderRadius: 2,
						}}
					/>
					<span style={{fontSize: 12, lineHeight: 1.4, color: '#555'}}>
						<strong style={{color: '#333'}}>{item.label}</strong>
						{' - '}
						{item.description}
					</span>
				</div>
			))}
		</div>
	);

	render() {
		const needsExtendToggle = this.state.items.some(
			(fe) => (fe.etaStops || []).length > 5
		);
		const maxStops = this.state.extendColumns
			? Math.max(5, ...this.state.items.map((fe) => (fe.etaStops || []).length), 5)
			: 5;
		const columnCount = 2 + maxStops;
		const columns = this.buildColumns();
		const tableData = this.buildTableData();

		return (
			<Card>
				<CardContent>
					<div className="table-bar" style={{marginBottom: 12}}>
						<div
							className="d-flex"
							style={{flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between'}}
						>
							<div style={{display: 'flex', flexWrap: 'wrap', gap: 12, flex: 1, zIndex: 999}}>
								{!this.state.isFeOnly && (
									<>
										<div style={{minWidth: 220, flex: 1}}>
											<label style={{fontSize: 12, marginBottom: 0}}>Regions</label>
											<Select
												isMulti
												options={this.state.regions}
												value={this.state.selectedRegions}
												onChange={(selectedRegions) => {
													const next = selectedRegions || [];
													this.setState({selectedRegions: next});
													this.fetchDailyEta(false, {selectedRegions: next});
												}}
												placeholder="All regions"
											/>
										</div>
										<div style={{minWidth: 220, flex: 1}}>
											<label style={{fontSize: 12, marginBottom: 0}}>Field Engineers</label>
											<Select
												key={this.state.feSelectOptions.length}
												isMulti
												options={this.state.feSelectOptions}
												value={this.state.selectedFes}
												onChange={(selectedFes) => {
													const next = selectedFes || [];
													this.setState({selectedFes: next});
													this.fetchDailyEta(false, {selectedFes: next});
												}}
												placeholder="All engineers"
												formatGroupLabel={(group) => (
													<span
														style={{
															fontSize: 11,
															fontWeight: 600,
															color: '#888',
															textTransform: 'uppercase',
														}}
													>
														{group.label}
													</span>
												)}
											/>
										</div>
									</>
								)}
							</div>
							<div style={{display: 'flex', alignItems: 'center', gap: 8}}>
								{needsExtendToggle && (
									<FormControlLabel
										control={
											<Switch
												checked={this.state.extendColumns}
												onChange={(e) =>
													this.setState({extendColumns: e.target.checked})
												}
												color="primary"
											/>
										}
										label="Show >5 ETAs"
									/>
								)}
								<Button color="primary" outline size="sm" onClick={() => this.fetchDailyEta()}>
									Refresh
								</Button>
							</div>
						</div>
					</div>

					{!this.state.isFeOnly && (
						<>
							<p style={{fontSize: 12, color: '#444', fontWeight: 600, marginBottom: 6}}>
								Times shown are in each field engineer&apos;s local time - not Arizona time.
							</p>
							<p style={{fontSize: 12, color: '#666'}}>
								SoD = first Heading to Site today (N/A until then). Customer names show when ETA is not yet
								available. Remotes have 0 drive, revisits assume 0.5hr onsite (others 2hr). * = actual arrival. Hover an ETA for details.
								Only engineers with appointments today are shown.
							</p>
						</>
					)}

					{this.renderLegendSection()}

					<ReactTable
						key={`daily-eta-${tableData.length}-${this.state.selectedRegions.map((r) => r.value).join(',')}-${this.state.selectedFes.map((f) => f.value).join(',')}`}
						columns={columns}
						data={tableData}
						loading={this.state.loading}
						loadingText="Loading Daily ETA…"
						showPagination={false}
						defaultPageSize={Math.max(tableData.length, 1)}
						pageSize={Math.max(tableData.length, 1)}
						minRows={0}
						className="-striped -highlight"
						getTrProps={(state, rowInfo) => {
							if (rowInfo?.row?._original?._rowType === 'region') {
								return {style: {background: '#eceff1'}};
							}
							return {};
						}}
						getTdProps={(state, rowInfo, column) => {
							if (rowInfo?.row?._original?._rowType === 'region') {
								if (column.id === 'name') {
									return {colSpan: columnCount};
								}
								return {style: {display: 'none'}};
							}
							if (this.state.isFeOnly && rowInfo?.original && column?.id) {
								const match = /^eta_(\d+)$/.exec(column.id);
								if (match) {
									const stop = rowInfo.original.etaStops
										&& rowInfo.original.etaStops[Number(match[1]) - 1];
									if (stop) {
										return {
											style: {cursor: 'pointer'},
											onClick: (event) => {
												if (event?.stopPropagation) event.stopPropagation();
												this.openStopPopover(rowInfo.original.name, stop);
											},
										};
									}
								}
							}
							return {};
						}}
					/>
					<Framework.Elements.LoadingSpinner isLoading={this.state.loading}/>
				</CardContent>

				<Modal
					isOpen={!!this.state.stopPopover}
					toggle={this.closeStopPopover}
					backdrop
				>
					<ModalBody>
						{this.state.stopPopover
							? this.renderStopTooltip(this.state.stopPopover.feName, this.state.stopPopover.stop)
							: null}
					</ModalBody>
					<ModalFooter>
						<Button color="secondary" onClick={this.closeStopPopover}>
							Close
						</Button>
					</ModalFooter>
				</Modal>

				{!this.state.isFeOnly && (
				<Modal
					isOpen={this.state.show_hd_modal}
					toggle={this.toggleHdModal}
					size="xl"
					backdrop
					style={{opacity: 1, maxWidth: window.innerWidth * 0.9}}
				>
					<ModalBody>
						<div className="form-group">
							{this.state.field_engineer ? (
								<EngineerHDCard
									history={this.props.history}
									currentDay={this.state.filterDate}
									engineer={this.state.field_engineer}
									refreshData={async () => {
										await this.getHelpdesks(this.state.field_engineer?.pkey);
									}}
								/>
							) : (
								<p>No helpdesk data available for this field engineer.</p>
							)}
						</div>
					</ModalBody>
					<ModalFooter>
						<Button color="secondary" onClick={this.toggleHdModal}>
							Close
						</Button>
					</ModalFooter>
				</Modal>
				)}
			</Card>
		);
	}
}
